import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AUTHZ_KEY, queryClient } from '@/shared/api/queryClient'
import { ApiError } from '@/shared/api/client'
import { msalInstance } from '@/features/auth/lib/msalConfig'
import { authApi } from '@/features/auth/api/authApi'
import type { AuthMyProfileResponse, RoleStatusUser, UserPermissions } from '@/features/auth/types'

export { AUTHZ_KEY }

/** Key of the legacy 24h localStorage snapshot, purged on load and on logout. */
const LEGACY_CACHE_KEY = 'ir-portal:auth-profile'

/** Marks that USER_LOGGED_IN has already been audited for this account in this tab. */
const LOGIN_FLAG_PREFIX = 'ir-portal:profile-logged:'

/** Deny-everything baseline used whenever authorization state is unknown. */
const NO_PERMISSIONS: UserPermissions = {
  canUploadFiles: false,
  canGenerateStatements: false,
  canDraftEmails: false,
  canSendEmails: false,
  canViewReports: false,
  canManageUsers: false,
  canDeleteBatches: false,
}

/**
 * Whether GET /check-role-status is deployed in this environment.
 * The function exists in the ir-api repo but is not registered in src/index.js,
 * so it 404s until the back-end ships it. Detected once, then cached.
 */
let liveSyncAvailable = true

export function isLiveSyncAvailable(): boolean {
  return liveSyncAvailable
}

export function markLiveSyncUnavailable(): void {
  if (!liveSyncAvailable) return
  liveSyncAvailable = false
  console.warn(
    '[authz] /check-role-status returned 404 — falling back to Auth_GetMyProfile. ' +
      'Role changes will NOT propagate live until that route is registered in ir-api/src/index.js.',
  )
}

/** SQL `bit` columns can surface as 1/0; coerce so `can()` never sees a truthy non-boolean. */
function toBool(value: unknown): boolean {
  return value === true || value === 1
}

function normalizePermissions(raw: Partial<UserPermissions> | undefined): UserPermissions {
  if (!raw) return NO_PERMISSIONS
  return {
    canUploadFiles: toBool(raw.canUploadFiles),
    canGenerateStatements: toBool(raw.canGenerateStatements),
    canDraftEmails: toBool(raw.canDraftEmails),
    canSendEmails: toBool(raw.canSendEmails),
    canViewReports: toBool(raw.canViewReports),
    canManageUsers: toBool(raw.canManageUsers),
    canDeleteBatches: toBool(raw.canDeleteBatches),
  }
}

export function normalizeRoleStatusUser(user: RoleStatusUser): RoleStatusUser {
  return {
    ...user,
    isActive: toBool(user.isActive),
    permissions: normalizePermissions(user.permissions),
  }
}

/** Map the Auth_GetMyProfile payload onto the same shape check-role-status returns. */
function fromProfile(res: AuthMyProfileResponse): RoleStatusUser {
  const user = res.user
  // A deactivated user comes back as success:false + deactivated:true, with no permissions.
  const deactivated = res.success === false || res.deactivated === true || user?.isActive === false

  return {
    userId: user?.userId ?? '',
    email: user?.email ?? '',
    displayName: user?.displayName ?? '',
    role: user?.role ?? '',
    isActive: !deactivated,
    permissions: deactivated ? NO_PERMISSIONS : normalizePermissions(user?.permissions),
    updatedAt: new Date().toISOString(),
  }
}

function loginFlagKey(): string | null {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  return account ? `${LOGIN_FLAG_PREFIX}${account.homeAccountId}` : null
}

/**
 * Records the login in the audit trail exactly once per account per tab.
 * Fire-and-forget: authorization truth always comes from check-role-status,
 * so a failure here must never gate the UI.
 */
function auditLoginOnce(): void {
  const key = loginFlagKey()
  if (!key || sessionStorage.getItem(key)) return
  sessionStorage.setItem(key, '1')
  void authApi.getMyProfile().catch(() => {
    sessionStorage.removeItem(key)
  })
}

/** Record that the login has already been audited, without issuing another call. */
function markLoginAudited(): void {
  const key = loginFlagKey()
  if (key) sessionStorage.setItem(key, '1')
}

async function fetchAuthz(): Promise<RoleStatusUser> {
  if (liveSyncAvailable) {
    try {
      const res = await authApi.checkRoleStatus()
      auditLoginOnce()
      return normalizeRoleStatusUser(res.currentUser)
    } catch (err) {
      // Only a missing route is recoverable. 401/403/500 must still fail closed.
      if (!(err instanceof ApiError && err.status === 404)) throw err
      markLiveSyncUnavailable()
    }
  }

  // Fallback: Auth_GetMyProfile is authoritative for role and permissions, but it
  // writes USER_LOGGED_IN on every call, so it is fetched on demand and never polled.
  const profile = await authApi.getMyProfile()
  markLoginAudited() // this call already recorded the login
  return fromProfile(profile)
}

/**
 * Flush every trace of authorization state.
 * Called on logout so a subsequent sign-in on the same machine can never
 * inherit the previous user's role.
 */
export function clearAuthProfileCache() {
  for (const key of Object.keys(sessionStorage)) {
    if (key.startsWith(LOGIN_FLAG_PREFIX)) sessionStorage.removeItem(key)
  }
  localStorage.removeItem(LEGACY_CACHE_KEY)
  queryClient.removeQueries({ queryKey: AUTHZ_KEY })
}

// Drop any snapshot written by the previous localStorage-backed implementation
// so existing browsers don't keep a stale role for up to 24 hours after deploy.
localStorage.removeItem(LEGACY_CACHE_KEY)

/**
 * Live authorization state for the signed-in user.
 *
 * Fails closed: while loading, on error, and for a disabled account, every
 * permission reads as false. Client-side gating is a UX layer only — the API
 * must independently authorize every privileged request.
 */
export function useAuthz() {
  const { data, isLoading, isError } = useQuery({
    queryKey: AUTHZ_KEY,
    queryFn: fetchAuthz,
    retry: 1,
    staleTime: 30_000,
    // Deliberately not 0: when running on the Auth_GetMyProfile fallback, letting the
    // entry be evicted between route transitions would re-fire USER_LOGGED_IN audit
    // writes. This cache is in-memory only and is cleared on logout.
    gcTime: 5 * 60_000,
  })

  const resolved = !isLoading && !isError && !!data
  const isActive = resolved && data.isActive === true
  const permissions = isActive ? data.permissions : NO_PERMISSIONS

  // Stable identity so consumers can safely use `can` as a hook dependency.
  const can = useCallback(
    (permission: keyof UserPermissions) => permissions[permission] === true,
    [permissions],
  )

  return {
    isLoading,
    isError,
    resolved,
    isActive,
    role: resolved ? data.role : null,
    displayName: data?.displayName ?? null,
    /** Full backend record, for read-only surfaces such as the profile page. */
    account: resolved ? data : null,
    permissions,
    can,
  }
}
